'use client';
import Image from '@/components/Image';
import { useIrclickidCheck } from '@/hooks/useIrclickidCheck';
import { useReporting } from '@/hooks/useReporting';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import { addSearchParams } from '@/utils/tools';
import { CloseOutlined, SearchOutlined, WapMoreOutlined } from '@zeelool/icon';
import Link from 'next/link';
import { useTranslations } from '@/i18n/use-translations';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { HeaderProps } from '.';
import AccountIcon from './components/AccountIcon';
import CartIcon from './components/CartIcon';
import DingTong from './components/DingTong';
import SearchPopup from './components/SearchComponent/SearchPopup.m';
import LeftSideNavWrapper from './components/SideNavWrapper.m';
import { Suspense } from 'react';
import { shouldShowTermsUpdate } from './terms-update';

const Header = (
  props: HeaderProps & {
    cartProductNum: number | string;
    deviceType: EPlatform;
    isShowHeader: boolean;
    isDarkMode: boolean;
    isShowDtBannerByPage: boolean;
    isShowAppDownloadBanner: boolean;
    loadSearchDataIfNeeded: () => Promise<void>;
    isBot?: boolean;
  },
) => {
  const {
    deviceType,
    isShowHeader,
    isDarkMode,
    isShowDtBannerByPage,
    isShowAppDownloadBanner,
    loadSearchDataIfNeeded,
  } = props;
  // 使用路由元数据Hook
  const { cnName } = useRouteMetadata();
  const [downloadVisible, setDownloadVisible] = useState(true);
  const [showSearchPopup, setShowSearchPopup] = useState(false);
  const globalHeaderRef = useRef<HTMLDivElement>(null);
  const setIsOpenSideNav = useStore((state) => state.setIsOpenSideNav);
  const isOpenSideNav = useStore((state) => state.isOpenSideNav);
  const serverTimestamp = useStore((state) => state.publicResourceData?.info?.timestamp);
  const showTermsUpdate = shouldShowTermsUpdate(serverTimestamp);
  const router = useRouter();
  const tA11y = useTranslations('layout.header.a11y');
  const tFloat = useTranslations('layout.rightBottomFloating');

  const { trackEvent } = useReporting();
  const timer = useRef<NodeJS.Timeout | null>(null);

  // 使用 irclickid 检查 Hook
  const { isIrclickid, getWebUrlWithIrclickid } = useIrclickidCheck();

  useEffect(() => {
    return () => {
      if (timer.current) {
        clearInterval(timer.current);
      }
    };
  }, []);

  const handleAppDownload = () => {
    trackEvent(
      {
        eventName: 'DownloadAppBar_Click',
        properties: {
          page_name: cnName,
          url_path: window.location.href,
        },
      },
      [ProviderType.SENSORS],
    );

    // 处理web_url，如果当前URL没有irclickid，尝试添加
    const webUrlWithIrclickid = getWebUrlWithIrclickid(window.location.href);

    window.location.href = addSearchParams('https://zeelool.onelink.me/QxSq/k76k1dbw', {
      pub_zl_utm_source:
        window.latestChannelAttribute?.expires > Date.now()
          ? window.latestChannelAttribute?.pub_zl_utm_source || undefined
          : undefined,
      pub_zl_utm_medium:
        window.latestChannelAttribute?.expires > Date.now()
          ? window.latestChannelAttribute?.pub_zl_utm_medium || undefined
          : undefined,
      pub_zl_utm_campaign:
        window.latestChannelAttribute?.expires > Date.now()
          ? window.latestChannelAttribute?.pub_zl_utm_campaign || undefined
          : undefined,
      pub_zl_utm_campaignid:
        window.latestChannelAttribute?.expires > Date.now()
          ? window.latestChannelAttribute?.pub_zl_utm_campaignid || undefined
          : undefined,
      pub_zl_referrer:
        window.latestChannelAttribute?.expires > Date.now()
          ? window.latestChannelAttribute?.pub_zl_referrer || undefined
          : undefined,
      pub_zl_page:
        window.latestChannelAttribute.expires > Date.now() ||
        window.latestChannelAttribute.expires === -1
          ? window.latestChannelAttribute.pub_zl_page || undefined
          : undefined,
      web_url: webUrlWithIrclickid,
    });
  };

  // 图标颜色类名
  const iconColorClass = isDarkMode ? 'text-white' : 'text-primary-black';

  const SDSearchEntryBtn_Click = () => {
    trackEvent(
      {
        eventName: 'SearchEntryBtn_Click',
        properties: {
          page_name: cnName,
          url_path: window.location.href,
        },
      },
      [ProviderType.SENSORS],
    );
    trackEvent(
      {
        eventName: 'TopNavTab_Click',
        properties: {
          tab_name: '搜索',
          page_name: cnName,
          url_path: window.location.href,
          tab_index: 1,
        },
      },
      [ProviderType.SENSORS],
    );
  };

  return (
    <div
      id="header"
      ref={globalHeaderRef}
      className={`HeaderSection sticky top-0 z-[400] w-full box-border ${
        !isShowHeader ? 'hidden' : ''
      } ${
        isDarkMode
          ? 'bg-[rgba(0,0,0,0.7)] shadow-none'
          : 'bg-primary-white shadow-[0px_0px_9px_0px_rgba(221,221,221,0.5)]'
      }`}
    >
      <Link href="/accessibility-statement" className="sr-only">
        Accessibility Statement
      </Link>
      {/* APP下载提示信息 */}
      {env.NEXT_PUBLIC_HAS_APP === 'true' && (
        <InView
          as="div"
          triggerOnce
          onChange={(inView) => {
            if (inView) {
              trackEvent(
                {
                  eventName: 'DownloadAppBar_Exp',
                  properties: {
                    page_name: cnName,
                    url_path: window.location.href,
                  },
                },
                [ProviderType.SENSORS],
              );
            }
          }}
          className={`flex items-center p-[0_3.733vw] w-full transition-all duration-300 bg-[#000000] ${
            isIrclickid
              ? "bg-[url('/zeelool-next/home-page/top-app-download2.png')]"
              : "bg-[url('/zeelool-next/home-page/top-app-download.png')]"
          } bg-no-repeat bg-[position:top_left] bg-[size:100%_100%] ${
            downloadVisible && isShowAppDownloadBanner ? 'h-[17.1vw]' : 'h-0 overflow-hidden'
          }`}
        >
          <button
            type="button"
            className="ml-[-1.600vw] flex cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-primary-white"
            onClick={() => setDownloadVisible(false)}
            aria-label={tA11y('dismissAppDownloadBanner')}
          >
            <CloseOutlined className="text-[7.467vw]" aria-hidden />
          </button>
          <button
            id={deviceType === EPlatform.IOS ? 'header-app-ios' : 'header-app-android'}
            className="ml-auto flex items-center justify-center w-[51px] h-[26px] text-white bg-transparent rounded-[13px] font-bold p-0 border-none text-xs"
            aria-label="Download APP"
            onClick={handleAppDownload}
          />
        </InView>
      )}
      {/* 顶通信息 */}
      {!!props.dtInfo && isShowDtBannerByPage && <DingTong {...props} />}
      {/* 导航栏信息 */}
      <div
        className={`w-full h-[11.733vw] flex justify-between items-center ${
          isDarkMode ? 'bg-[rgba(0,0,0,0.7)] shadow-none' : 'bg-primary-white'
        }`}
        // 这些代码是防止在Ios系统下滚动起来，整个导航栏的图标都会上下跳动，包括CartIcon和AccountIcon都加了对应的样式
        style={{
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
          perspective: '1000px',
        }}
      >
        <ul
          className="flex items-center w-[21.333vw] pl-[3.733vw] box-border"
          style={{ transform: 'translateZ(0)' }}
        >
          <li className="flex items-center justify-center h-[6.400vw] w-[6.400vw] mr-[5.333vw]">
            <button
              type="button"
              aria-label={tA11y('openMenu')}
              aria-expanded={isOpenSideNav}
              aria-controls="mobile-side-nav-panel"
              className="relative p-0 m-0 border-0 bg-transparent flex items-center justify-center h-full w-full cursor-pointer"
              onClick={() => {
                setIsOpenSideNav(true);
                trackEvent(
                  {
                    eventName: 'TopNavTab_Click',
                    properties: {
                      tab_name: '左侧菜单按钮',
                      page_name: cnName,
                      url_path: window.location.href,
                      tab_index: 0,
                    },
                  },
                  [ProviderType.SENSORS],
                );
              }}
            >
              <WapMoreOutlined className={`text-[6.400vw] ${iconColorClass}`} aria-hidden />
              {showTermsUpdate && (
                <span
                  className="absolute right-0 top-0 h-[2.133vw] w-[2.133vw] rounded-full bg-primary-purple"
                  aria-hidden
                />
              )}
            </button>
          </li>
          <li className="flex items-center justify-center h-[6.400vw] w-[6.400vw]">
            <button
              type="button"
              aria-label={tA11y('openSearch')}
              className="p-0 m-0 border-0 bg-transparent flex items-center justify-center h-full w-full cursor-pointer"
              onClick={async () => {
                loadSearchDataIfNeeded();
                setShowSearchPopup(true);
                SDSearchEntryBtn_Click();
              }}
            >
              <SearchOutlined className={`text-[6.400vw] ${iconColorClass}`} aria-hidden />
            </button>
          </li>
        </ul>
        <Link
          prefetch={false}
          aria-label="Zeelool Logo"
          href="/"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (window.location.pathname === '/') {
              return;
            }
            trackEvent(
              {
                eventName: 'TopNavTab_Click',
                properties: {
                  tab_name: 'Logo',
                  page_name: cnName,
                  url_path: window.location.href,
                  tab_index: 2,
                },
              },
              [ProviderType.SENSORS],
            );
            router.push('/');
          }}
          className="h-[11.733vw] w-[38.933vw] relative flex items-center justify-center"
        >
          {props.logoData?.logo_image && (
            <Image
              src={isDarkMode ? '/white-logo.png' : props.logoData.logo_image}
              alt="Zeelool Logo"
              fill
              fetchPriority="high"
              priority
            />
          )}
          {props.logoData?.logo_sub_image && (
            <Image src={props.logoData.logo_sub_image} alt="logo" fill />
          )}
        </Link>

        <ul
          className="flex items-center h-full w-[21.333vw] box-border pr-[3.733vw]"
          style={{ transform: 'translateZ(0)' }}
        >
          <AccountIcon isDarkMode={isDarkMode} />
          <CartIcon isDarkMode={isDarkMode} cartProductNum={props.cartProductNum} />
        </ul>
      </div>
      <LeftSideNavWrapper navData={props.headerNavList} isBot={props.isBot ?? false} />
      <Suspense fallback={null}>
        <SearchPopup show={showSearchPopup} onClose={() => setShowSearchPopup(false)} />
      </Suspense>
    </div>
  );
};

export default Header;
